import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ROLE_BADGE } from "@/lib/format";
import { BookMarked, ShoppingCart, ShoppingBag, ArrowRightLeft, DollarSign, Boxes, Calendar, Download, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ENTITY = {
  event: { label: "Event", icon: Calendar, cls: "bg-slate-100 text-slate-700" },
  product: { label: "Master Produk", icon: BookMarked, cls: "bg-cyan-50 text-cyan-700" },
  catalog: { label: "Katalog", icon: BookMarked, cls: "bg-indigo-50 text-indigo-700" },
  order: { label: "Order", icon: ShoppingCart, cls: "bg-blue-50 text-blue-700" },
  purchase: { label: "Belanja", icon: ShoppingBag, cls: "bg-teal-50 text-teal-700" },
  substitution: { label: "Substitusi", icon: ArrowRightLeft, cls: "bg-purple-50 text-purple-700" },
  adjustment: { label: "Adjustment", icon: DollarSign, cls: "bg-amber-50 text-amber-700" },
  cost: { label: "Biaya", icon: DollarSign, cls: "bg-rose-50 text-rose-700" },
  box: { label: "Box", icon: Boxes, cls: "bg-emerald-50 text-emerald-700" },
};
const ACTION = {
  create: "membuat", update: "mengubah", delete: "menghapus", validate_payment: "memvalidasi pembayaran",
  update_sell_price: "mengubah harga jual", update_bought: "update qty dibeli", update_status: "mengubah status item",
  approve: "memproses approval", settle: "menyelesaikan", ship: "mengirim",
};
const dayLabel = (iso) => {
  const d = new Date(iso); const t = new Date(); const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return "Hari ini";
  if (d.toDateString() === y.toDateString()) return "Kemarin";
  return d.toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

export default function Activity() {
  const { event, eventId } = useOutletContext();
  const [logs, setLogs] = useState([]);
  const [entity, setEntity] = useState("all");
  const [who, setWho] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setLogs((await api.get(`/events/${eventId}/activity`)).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const people = useMemo(() => Object.values(Object.fromEntries(logs.map((l) => [l.user_id, { id: l.user_id, name: l.user_name }]))), [logs]);
  const filtered = logs.filter((l) => (entity === "all" || l.entity === entity) && (who === "all" || l.user_id === who) &&
    (!q || `${l.user_name} ${l.entity_label} ${l.detail} ${l.action}`.toLowerCase().includes(q.toLowerCase())));
  const groups = filtered.reduce((acc, l) => { const k = dayLabel(l.timestamp); (acc[k] ||= []).push(l); return acc; }, {});

  const exportCsv = () => {
    const rows = [["waktu", "user", "role", "aksi", "entitas", "label", "detail"], ...filtered.map((l) => [l.timestamp, l.user_name, l.user_role, l.action, l.entity, l.entity_label || "", l.detail || ""])];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `aktivitas-${event?.code}.csv`; a.click();
  };

  return (
    <div>
      <PageHeader title="Log Aktivitas" subtitle={`Siapa mengubah apa di event ${event?.code}. ${filtered.length} aktivitas.`}>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="activity-refresh" onClick={load} disabled={loading}><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="activity-export" onClick={exportCsv}><Download className="w-4 h-4" /> Export CSV</Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Input data-testid="activity-search" placeholder="Cari nama, item, detail..." value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64" />
        <Select value={entity} onValueChange={setEntity}>
          <SelectTrigger data-testid="activity-filter-entity" className="h-9 w-44"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua modul</SelectItem>{Object.entries(ENTITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={who} onValueChange={setWho}>
          <SelectTrigger data-testid="activity-filter-user" className="h-9 w-48"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua anggota</SelectItem>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {filtered.length === 0 && <div className="text-center py-16 text-slate-400 text-sm" data-testid="activity-empty">Belum ada aktivitas untuk filter ini.</div>}

      <div className="space-y-8" data-testid="activity-timeline">
        {Object.entries(groups).map(([day, items]) => (
          <section key={day}>
            <div className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur py-1 mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">{day} <span className="font-mono text-slate-400">· {items.length}</span></div>
            <ol className="relative border-l-2 border-slate-200 ml-3 space-y-1">
              {items.map((l) => {
                const e = ENTITY[l.entity] || { label: l.entity, icon: Calendar, cls: "bg-slate-100 text-slate-600" };
                const Icon = e.icon;
                return (
                  <li key={l.id} className="relative pl-6 py-2 group" data-testid={`activity-${l.id.slice(0, 8)}`}>
                    <span className={`absolute -left-[13px] top-2.5 w-6 h-6 rounded-full grid place-items-center ring-4 ring-slate-50 ${e.cls}`}><Icon className="w-3 h-3" /></span>
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm">
                      <span className="font-semibold text-slate-900">{l.user_name}</span>
                      <span className={`text-[9px] font-mono px-1 rounded ${ROLE_BADGE[l.user_role] || "bg-slate-200"}`}>{l.user_role?.toUpperCase()}</span>
                      <span className="text-slate-600">{ACTION[l.action] || l.action}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${e.cls}`}>{e.label}</span>
                      {l.entity_label && <span className="font-medium text-slate-800">{l.entity_label}</span>}
                      <span className="ml-auto text-[11px] font-mono text-slate-400">{new Date(l.timestamp).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    {l.detail && <div className="text-xs text-slate-500 mt-0.5 font-mono break-all">{l.detail}</div>}
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}
